import { Box, Flex } from '@lego/klik-ui';
import { Navigate, useParams } from 'react-router-dom';
import { Loader, PageLoadError } from '@/components/common';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { DeploymentTable } from '@/components/deployment';
import { FleetInvitationsTable } from '@/components/device/FleetInvitationsTable';
import { FleetDetails, FleetDeviceTable } from '@/components/fleet';
import { useDocumentTitle } from '@/hooks/common';
import { useFleet } from '@/hooks/fleet';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { paths } from '@/utility/global_paths';

const FleetPageContent = ({ workspaceId, fleetId }: { workspaceId: number; fleetId: number }) => {
  const { data, error, isLoading, mutate } = useFleet(workspaceId, fleetId);
  useRegisterPageContext(data);
  useDocumentTitle(data ? `Fleet #${data.id}` : '');

  if (isLoading) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <PageLoadError
        backAction={{ label: 'Back to workspace', to: paths.workspaceById(workspaceId) }}
        onRetry={mutate}
      />
    );
  }

  const fleet = data;

  return (
    <Box>
      <Box>
        <Box>
          <BreadCrumb
            crumbs={[
              { label: 'Workspaces', path: paths.workspaces },
              {
                label: `Workspace #${fleet.workspaceId}`,
                path: paths.workspaceById(fleet.workspaceId),
              },
              {
                label: `Fleet #${fleet.id}`,
                path: paths.fleetById(fleet.workspaceId, fleet.id),
              },
            ]}
          />
        </Box>
        <Flex direction="column" gap={8} marginY={4}>
          <FleetDetails fleet={fleet} />
          <FleetDeviceTable fleet={fleet} />
          <DeploymentTable fleet={fleet} />
          <FleetInvitationsTable fleet={fleet} />
        </Flex>
      </Box>
    </Box>
  );
};

export const FleetPage = () => {
  const { workspaceId, fleetId } = useParams();

  if (
    !workspaceId ||
    !Number.parseInt(workspaceId, 10) ||
    !fleetId ||
    !Number.parseInt(fleetId, 10)
  ) {
    return <Navigate to={paths.workspaces} />;
  }

  return (
    <FleetPageContent
      fleetId={Number.parseInt(fleetId, 10)}
      workspaceId={Number.parseInt(workspaceId, 10)}
    />
  );
};
